import { createApi } from '@reduxjs/toolkit/query/react';
import type { CustomColumnConfig, ProspectEnrichmentEntry } from '@/types';
import { baseQueryWithAuth } from '../base-query';

// Plain "add lead" endpoints. Distinct from /enrichment/* so the backend
// never routes through provider calls for these requests — chosen over a
// skip_enrichment boolean on the enrichment endpoints because a flipped
// flag (intentionally or via a crafted payload) would still hit the
// enrichment codepath and could leak email/phone via the WS result.
export interface AddLeadManuallyRequest {
  lead_list_id: string;
  linkedin_url?: string;
  first_name?: string;
  last_name?: string;
  /** Accepts either a domain or a plain company name. */
  company_domain?: string;
  company_name?: string;
  /** With native_sequencing_v1, a valid email or phone makes the person the
   *  org's own lead, with no lookup. */
  email?: string;
  phone?: string;
  /** Kept for a person with their own email or phone. */
  job_title?: string;
}

export interface AddLeadManuallyResponse {
  success: boolean;
  lead_id?: string;
  message?: string;
}

export interface AddLeadsBulkRequest {
  lead_list_id: string;
  prospects: ProspectEnrichmentEntry[];
  custom_column_definitions?: CustomColumnConfig[];
  raw_person_ids?: string[];
  /** Keep-every-column import: a Lead for every row instead of a staged
   *  RawPerson. The backend ignores it unless the org has Agents. */
  create_leads?: boolean;
}

export interface AddLeadsBulkResponse {
  success: boolean;
  added_count: number;
  duplicates_skipped?: number;
  lead_ids?: string[];
}

export const leadsApi = createApi({
  reducerPath: 'leadsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Leads'],
  endpoints: (builder) => ({
    addLeadManually: builder.mutation<
      AddLeadManuallyResponse,
      AddLeadManuallyRequest
    >({
      query: (body) => ({
        url: '/leads/manual',
        method: 'POST',
        body,
      }),
    }),
    addLeadsBulk: builder.mutation<AddLeadsBulkResponse, AddLeadsBulkRequest>({
      query: (body) => ({
        url: '/leads/bulk',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const { useAddLeadManuallyMutation, useAddLeadsBulkMutation } = leadsApi;
